import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useFormContext } from 'react-hook-form';
import {
  useTakeoverExperimentContext,
  useVariantCardContext,
  useVariantConfigurationContext,
} from 'modules/experiments/contexts';
import {
  getTakeoverCampaignMessageMedium,
  getTakeoverExperimentType,
} from 'modules/experiments/ducks/selectors';
import { dataTestId, deletingCardState } from 'modules/experiments/constants';
import { useExperimentsUserPermission } from 'modules/experiments/hooks';
import { getVariantFormFieldValue } from 'modules/experiments/utils';
import { VariantCardWrapper } from './Styled';
import { VariantHeader } from './Header';
import { VariantBody } from './body';
import { EditableCardAction, LoadingFooter, NonEditableCardAction, STOCardAction } from './footer';

const VariantFooter = () => {
  const { isCardInEditMode, deletingCard } = useVariantCardContext();
  const { isVariantNotEditable, isSTOExperiment } = useTakeoverExperimentContext();

  switch (true) {
    // Loading animation takes precedence. So, needs to be the first case.
    case isCardInEditMode && deletingCardState.includes(deletingCard):
      return <LoadingFooter />;

    case isCardInEditMode && !isVariantNotEditable:
      return <EditableCardAction />;

    case isCardInEditMode && isVariantNotEditable && !isSTOExperiment:
      return <NonEditableCardAction />;

    case isCardInEditMode && isSTOExperiment:
      return <STOCardAction />;

    default:
      return <></>;
  }
};

export const VariantCard = () => {
  const type = useSelector(getTakeoverExperimentType);
  const messageMedium = useSelector(getTakeoverCampaignMessageMedium);
  const { viewOnlyPermission } = useExperimentsUserPermission(messageMedium);
  const {
    variant,
    deletingCard,
    isCardInEditMode,
    isCardInPreviewMode,
    isCardSelected,
    selectCard,
  } = useVariantCardContext();
  const { selectedVariant } = useVariantConfigurationContext();
  const {
    isSendTimeExperiment,
    unsavedChanges: { setUnsavedVariantList },
    formActions: { resetDirtyVariantCard },
  } = useTakeoverExperimentContext();
  const {
    formState: { isDirty },
    reset,
  } = useFormContext();
  const { control } = variant;
  const defaultVariantCardValue = useMemo(
    () => getVariantFormFieldValue({ variant, type, messageMedium }),
    [variant, type, messageMedium],
  );

  // Update the experiment's context if
  // the currently selected card becomes dirty
  useEffect(() => {
    if (isCardSelected) {
      setUnsavedVariantList(isDirty);
    }
  }, [isDirty, selectedVariant?.id]);

  // We want to reset any dirty forms whenever
  // dispatchResetDirtyVariantCard is called
  useEffect(() => {
    if (isDirty) reset(defaultVariantCardValue);
  }, [resetDirtyVariantCard]);

  return (
    <VariantCardWrapper
      data-test={dataTestId.variantCard}
      data-alphabet={variant.alphabet}
      isSendTimeExperiment={isSendTimeExperiment}
      borderColor={selectedVariant?.textColor || 'transparent'}
      isPreviewMode={isCardInPreviewMode}
      isEditMode={isCardInEditMode}
      isControl={control}
      isDeleteInProgress={deletingCard}
      isViewOnlyPermission={viewOnlyPermission}
      {...(!isCardInEditMode && {
        onClick: selectCard,
      })}
    >
      <VariantHeader />
      <VariantBody />
      <VariantFooter />
    </VariantCardWrapper>
  );
};
